import React, { useEffect, useState } from 'react'
import { AppTable, Field } from '../../components/table'
import { Box, Modal } from '@mui/material'
import { Actions, Pager } from '../../components/table/types'
import seeIcon from '../../assets/table_icons/view.svg'
import completeIcon from '../../assets/table_icons/completed.svg'
import deleteIcon from '../../assets/table_icons/delete.svg'
import editIcon from '../../assets/table_icons/edit.svg'
import { useTranslation } from 'react-i18next'
import { getTrainingContainer } from '../../container/training-module'
import { IUserTrainingService, USER_TRAINING_SERVICE_KEY } from '../../modules/training'
import { Query, QueryParam } from '../../common/api/Query'
import { ROUTE_TRAINING } from '../../routes/routes-constants'
import { navigate } from '@reach/router'
import {
  emptyUserTrainingDTO,
  UserTrainingDTO,
} from '../../modules/training/models/UserTrainingDTO'
import { UserTraining } from '../../modules/training/models/UserTraining'
import { getUserContainer } from '../../container/user-module'
import { UserService } from '../../modules/users/services/UserService'
import { LOGGED_USER_SERVICE_KEY, USER_SERVICE_KEY } from '../../modules/users'
import { User } from '../../modules/users/models/User'
import { ILoggedUserService } from '../../modules/users/services/LoggedUserService'
import { reduceString } from '../../common/utils/strings'
import { canCompleteTrainingNow } from '../../common/utils/date'
import { AppButton, ButtonTheme } from '../../components/app-button/AppButton'
import { AssignTrainingForm } from './AssignTrainingForm'
import { CustomModal } from '../../components/modal/CustomModal'
import { getAppContainer, STATUS_SERVICE_KEY } from '../../container/app'
import { IStatusService } from '../../common/status/StatusService'
import { Pagination } from '../../components/table/Pagination'
import { useUserRoles } from '../../modules/users/hooks/useUserRoles'
import { PageLayout } from 'pages/layout'
import styles from './Table.module.css'

const userContainer = getUserContainer()
const userService = userContainer.get<UserService>(USER_SERVICE_KEY)
const loggedUserService = userContainer.get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)
const userTrainingService =
  getTrainingContainer().get<IUserTrainingService>(USER_TRAINING_SERVICE_KEY)
const statusService = getAppContainer().get<IStatusService>(STATUS_SERVICE_KEY)

export interface TrainingTable extends UserTrainingDTO {
  title: string
  description: string
  image: string
  id: string
  trainingID: string
  startDate: Date
  finishDate: Date
  frecuency: number
  series: number
  repetitions: number
  frequencyString: string
  completed: boolean
  userID: string
}

type CompletedFilter = 'all' | 'completed' | 'notCompleted'

type TableTrainingsProps = {
  id?: string
}

export function Table(props: TableTrainingsProps): JSX.Element {
  const { t } = useTranslation('translation')
  const loggedUser = loggedUserService.get()
  const [user, setUser] = useState<User>()
  const [userTraining, setUserTraining] = useState<UserTrainingDTO[]>([])
  const [page, setPage] = useState<number>(0)
  const [pager, setPager] = useState<Pager>()
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [count, setCount] = useState<number>(0)
  const [completeTraining, setCompleteTraining] = useState<TrainingTable[]>([])
  const [currentUserTraining, setCurrentUserTraining] = useState<UserTrainingDTO>(
    emptyUserTrainingDTO()
  )
  const [openModalAssignTraining, setOpenModalAssignTraining] = useState<boolean>(false)
  const [edit, setEdit] = useState<boolean>(false)
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false)
  const [completedFilter, setCompletedFilter] = useState<CompletedFilter>('all')

  const {
    canCreateUserTraining,
    canEditUserTraining,
    canDeleteUserTraining,
    canCompleteUserTraining,
  } = useUserRoles(loggedUser)

  useEffect(() => {
    if (!loggedUser?.id) return
    userService.getByID(loggedUser?.id).subscribe((res) => {
      res && setUser(res)
    })
  }, [])

  useEffect(() => {
    const auxTable: TrainingTable[] = userTraining.map((ut) => ({
      title: t(ut.title ?? ''),
      description: t(ut.description ?? ''),
      image: ut.image ?? '',
      id: ut.id,
      trainingID: ut.trainingID,
      startDate: ut.startDate,
      finishDate: ut.finishDate,
      frecuency: ut.frecuency,
      series: ut.series,
      repetitions: ut.repetitions,
      frequencyString:
        ut.frecuency === 1
          ? t('morning')
          : ut.frecuency === 2
          ? t('afternoon')
          : ut.frecuency === 3
          ? t('night')
          : '',
      completed: ut.completed,
      userID: ut.userID,
    }))
    setCompleteTraining(auxTable)
    setIsLoading(false)

    return () => {
      setCompleteTraining([])
    }
  }, [userTraining, t])

  useEffect(() => {
    const userID = props.id ?? loggedUser?.id
    if (!userID) return

    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
    const startDateFilter = thirtyDaysAgo.toISOString().split('T')[0]

    const today = new Date()
    const finishDateFilter = today.toISOString().split('T')[0]

    userTrainingService
      .getFilteredList(
        new Query({
          query: [
            new QueryParam<UserTraining>('userID', userID),
            new QueryParam<UserTraining>('startDate', startDateFilter),
            new QueryParam<UserTraining>('finishDate', finishDateFilter),
          ],
          pager: { offset: page * rowsPerPage, limit: rowsPerPage },
          sort: [{ field: 'startDate', desc: true }],
        })
      )
      .subscribe((res) => {
        if (!res) return
        setUserTraining(res.items)
        setCount(res.count)
      })

    return () => {
      setUserTraining([])
    }
  }, [isLoading, openModalAssignTraining, page, rowsPerPage, props.id, loggedUser])

  const handleChangePage = (event: unknown, value: number) => setPage(value)

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (Number.isNaN(event.target.value)) {
      setRowsPerPage(10)
      return
    }
    setRowsPerPage(Number.parseInt(event.target.value))
  }

  useEffect(() => {
    setPager({
      page,
      count,
      handleChangePage,
      rowsPerPage,
      handleChangeRowsPerPage,
    })
  }, [page, count, rowsPerPage])

  const handlerSeeTraining = (ut: TrainingTable) => {
    if (!ut?.id) return
    navigate(`${ROUTE_TRAINING}/${ut.id}`)
  }

  const handlerEdit = (ut: UserTrainingDTO) => {
    setCurrentUserTraining(ut)
    setEdit(true)
    setOpenModalAssignTraining(true)
  }

  const handlerCompleteTraining = (a: UserTrainingDTO) => {
    userTraining.forEach((ut) => {
      if (a?.id === ut.id) {
        const newUserTraining = Object.assign({ ...a }, { completed: true })
        userTrainingService.update(newUserTraining).subscribe(() => setIsLoading(!isLoading))
      }
    })
  }

  const handlerCloseAssignTraining = () => {
    setOpenModalAssignTraining(false)
  }

  const removeTraining = (a: UserTrainingDTO) => {
    setCurrentUserTraining(a)
    setOpenDeleteModal(true)
  }

  const handleCloseDeleteModal = () => {
    setOpenDeleteModal(false)
  }

  const handleDeleteTraining = () => {
    if (currentUserTraining?.id)
      userTrainingService.delete(currentUserTraining.id).subscribe((_) => {
        statusService.sendStatus({ variant: 'success' })
        setIsLoading(true)
      })
    setOpenDeleteModal(false)
    setIsLoading(!isLoading)
  }

  const handleCompletedFilter = (filter: CompletedFilter) => {
    if (filter === completedFilter) return
    setCompletedFilter(filter)
    setPage(0)
  }

  const filteredTraining =
    completedFilter === 'all'
      ? completeTraining
      : completeTraining.filter((t) =>
          completedFilter === 'completed' ? t.completed : !t.completed
        )

  const fields: Field<TrainingTable>[] = [
    {
      name: 'title',
      label: t('title'),
    },
    {
      name: 'description',
      label: t('description'),
      renderFunc: (f, i) => reduceString(i.description ?? '', 30),
    },
    {
      name: 'startDate',
      label: t('date'),
      renderFunc: (f, i) => new Date(i.startDate).toLocaleDateString(),
    },
    {
      name: 'series',
      label: t('series'),
      renderFunc: (f, i) => (i.series ?? 0).toString(),
    },
    {
      name: 'repetitions',
      label: t('repetitions'),
      renderFunc: (f, i) => (i.repetitions ?? 0).toString(),
    },
    {
      name: 'frequencyString',
      label: t('frequency'),
    },
    {
      name: 'completed',
      label: t('completed'),
      renderFunc: (f, i) => {
        return (
          <span className={i.completed ? styles.completedStatus : styles.pendingStatus}>
            {t(i.completed ? 'completed' : 'notComplete')}
          </span>
        )
      },
    },
  ]

  const actions: Actions<any> = {
    actionsColumn: t('actions'),
    items: [
      {
        handler: handlerSeeTraining,
        icon: seeIcon,
        label: 'see',
      },
      {
        handler: (ut) => handlerCompleteTraining(ut),
        icon: completeIcon,
        label: 'complete',
        hidden: (ut: TrainingTable) =>
          !canCompleteUserTraining ||
          !!props.id ||
          !canCompleteTrainingNow(ut.startDate, ut.frecuency) ||
          ut.completed,
      },
      {
        handler: (ut) => handlerEdit(ut),
        icon: editIcon,
        label: 'edit',
        hidden: () => !canEditUserTraining || !props.id,
      },
      {
        handler: (ut) => removeTraining(ut),
        icon: deleteIcon,
        label: 'delete',
        hidden: () => !canDeleteUserTraining || !props.id,
      },
    ],
  }

  const filterButtons: { label: string; value: CompletedFilter }[] = [
    { label: t('all'), value: 'all' },
    { label: t('completed'), value: 'completed' },
    { label: t('notComplete'), value: 'notCompleted' },
  ]

  return (
    <PageLayout>
      <Modal open={openDeleteModal} onClose={handleCloseDeleteModal}>
        <CustomModal
          title={t('deleteTraining')}
          warningText={t('irreversibleTrainingAction')}
          handleClose={handleCloseDeleteModal}
          handleSave={handleDeleteTraining}
        />
      </Modal>
      {openModalAssignTraining && (
        <AssignTrainingForm
          id={props.id}
          trainingID={edit ? currentUserTraining.id : undefined}
          open={openModalAssignTraining}
          handleClose={handlerCloseAssignTraining}
        />
      )}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        }}
      >
        <div className={styles.tableTop}>
          <div className={styles.filterBar}>
            {filterButtons.map((btn) => (
              <button
                key={btn.value}
                className={`${styles.filterButton} ${
                  completedFilter === btn.value ? styles.filterButtonActive : ''
                }`}
                onClick={() => handleCompletedFilter(btn.value)}
              >
                {btn.label}
              </button>
            ))}
          </div>

          <div className={styles.tableScroll}>
            <AppTable
              fields={fields}
              items={filteredTraining}
              rowKeyField={'id'}
              actions={actions}
              isLoading={isLoading}
            />
          </div>

          {!isLoading && userTraining.length < 1 && (
            <p className={styles.emptyState}>{t('notExercisesAssigned')}</p>
          )}
        </div>

        <Box display={'flex'} justifyContent={'space-between'} width={'100%'} alignItems={'center'}>
          <Box sx={{ marginLeft: '20px' }}>{pager && <Pagination {...pager} />}</Box>
          {canCreateUserTraining && props.id && (
            <Box mr={5} display={'flex'} justifyContent={'flex-end'}>
              <AppButton
                label={t('assignTraining')}
                theme={ButtonTheme.NewPrimary}
                handler={() => {
                  setEdit(false)
                  setOpenModalAssignTraining(true)
                }}
                type={'button'}
              />
            </Box>
          )}
        </Box>
      </Box>
    </PageLayout>
  )
}
